import { useEffect, useState } from 'react';

import { useAtom } from 'jotai';
import { useParams } from 'react-router';

import { DealerSelectButton, useSelectedDealer } from '@lotlinx-aws/dealer-selector';
import { abbreviationIconHeight, sidebarPopoverAtom } from 'components/SideNavbar';
import useRouteTracker from 'hooks/useRouteTracker';
import ROUTES from 'routes';
import Flex from '@lotlinx-aws/sparky-component-library/Sparky/Flex';
import { LXBrainBradientV2 } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/LXBrainGradientV2';
import { LXBrainV2 } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/LXBrainV2';
import { userAtom } from 'store';
import { coDriverActiveAtom } from 'store/coDriverActiveAtom';

import styles from './TopNavbar.module.scss';
import useWindowSize from 'hooks/useWindowSize.tsx';
import { showMarketMapsAtom } from 'pages/MarketMaps';
import useFeatureFlag from 'hooks/useFeatureFlag.tsx';
import { FeatureFlag } from 'types/FeatureFlags.ts';
import IconButton from '@lotlinx-aws/sparky-component-library/Sparky/IconButton';
import { IconLibrary, IconMap } from '@tabler/icons-react';
import { COLOR } from '@lotlinx-aws/sparky-component-library/Sparky/styles/vars';
import { useNavigateKeepParams } from 'components/KeepParamsRouter/KeepParamsRouter';
import MarketInsightVersionNotification from 'components/TopNavbar/MarketInsightVersionNotification';
import { AccountType } from 'types/User';

/**
 * TopNavbar
 */
export default function TopNavbar() {
  const { vin } = useParams();
  const [popoverMenu] = useAtom(sidebarPopoverAtom);
  const [, setShowMarketMaps] = useAtom(showMarketMapsAtom);
  const [isCoDriverActive, setIsCoDriverActive] = useAtom(coDriverActiveAtom);
  const [isCoDriverHover, setIsCoDriverHover] = useState<boolean>(false);
  const [user] = useAtom(userAtom);
  const { dealer } = useSelectedDealer();
  const { height } = useWindowSize();
  const navigate = useNavigateKeepParams();
  const showMarketMaps = useFeatureFlag(FeatureFlag.ENABLE_MARKET_MAPS);
  // GA/GTM related application route/user tracking
  useRouteTracker(ROUTES);

  function returnAccountInfo() {
    return {
      visitor: { id: user?.id, email: user?.email },
      account: { dealerId: dealer?.id, accountName: dealer?.name, payingStatus: dealer?.type },
    };
  }

  //* Make the account information available in the inspector console to be brought into data layer for GTM
  useEffect(() => {
    if (dealer) {
      /* @ts-ignore */
      window.getAccountInfo = returnAccountInfo();
    }
  }, [dealer]);

  return (
    <div className={styles.topNavbar}>
      {!popoverMenu || height < abbreviationIconHeight ? (
        <DealerSelectButton
          align='start'
          verticalOffset={18}
          appId='Turn4'
          isInternalUser={user?.accountType === AccountType.OWNER}
          horizontalOffset={-5}
          data-track='topnav_dealer_select'
          data-track-section='header'
          data-track-target='open_dropdown'
        />
      ) : (
        <div />
      )}
      <Flex alignItems='center'>
        <MarketInsightVersionNotification />

        {vin ? (
          <Flex
            data-active={isCoDriverActive}
            data-hover={isCoDriverHover}
            data-track='topnav_codriver'
            data-track-section='header'
            data-track-target='toggle'
            alignItems='center'
            justifyContent='center'
            padding='8px'
            onClick={() => setIsCoDriverActive(!isCoDriverActive)}
            onMouseEnter={() => setIsCoDriverHover(true)}
            onMouseLeave={() => setIsCoDriverHover(false)}
            className={styles.coDriverIcon}
          >
            {isCoDriverHover && !isCoDriverActive ? (
              <LXBrainBradientV2 size={32} />
            ) : (
              <LXBrainV2
                size={32}
                color={
                  isCoDriverActive || isCoDriverHover ? 'var(--lex-logo)' : 'var(--icon-action)'
                }
              />
            )}
          </Flex>
        ) : import.meta.env.VITE_APP_DEPLOY_ENV !== 'demo' && showMarketMaps ? (
          <IconButton
            aria-label='Maps'
            size='lg'
            onClick={() => setShowMarketMaps((prev) => !prev)}
            tooltip='Market Maps'
            data-track='topnav_market_maps'
            data-track-section='header'
            data-track-target='toggle'
          >
            <IconMap size={32} color={COLOR.SPARKY_NAVY_400} aria-hidden={true} />
          </IconButton>
        ) : null}
      </Flex>
    </div>
  );
}
